import { deleteField, doc, getDoc } from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { AlertCircle, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { SectionLabel } from "components/AppShell";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { FileInput } from "components/ui/file-input";
import { Label } from "components/ui/label";
import { db } from "integrations/firebase/firestore";
import { storage } from "integrations/firebase/storage";

import {
  HERO_VIDEO_MAX_BYTES,
  HERO_VIDEO_MAX_SECONDS,
  HERO_VIDEO_MAX_WIDTH,
  HERO_VIDEO_TYPE,
  heroVideoPath,
} from "../../events/landing";
import { heroVideoProblem } from "../hero-video-check";
import type { EventSettings } from "../schemas";
import { isObjectMissing } from "../storage-errors";
import { useEventSettingsWrite } from "../use-event-settings-writes";

/** The recipe the guidance gives. Stated once, so the text and the copy agree. */
// The filter is quoted for a shell: the double quotes protect the parentheses
// and the commas, the single quotes are ffmpeg's own. `min(1280,iw)` keeps a
// source that is wider than 16:9 inside the width limit, which a fixed height
// would not, and `-2` picks the height that keeps the proportions and is even.
const ENCODE_COMMAND = `ffmpeg -i in.mov -an -vf "scale='min(1280,iw)':-2" -c:v libx264 -crf 28 -preset slow -movflags +faststart -t 10 out.mp4`;

type Phase = "idle" | "checking" | "uploading" | "removing";

/**
 * The looping clip behind the landing page hero.
 *
 * Storage rules bound the type and the size, so the browser checks the two
 * they cannot, duration and width, before it uploads anything. The shape is
 * the hero image card's: one fixed object per event so a replacement
 * overwrites, the document written after the upload, and removal that clears
 * the document first and the object second.
 *
 * The event's own image is the poster, so there is nothing separate to upload
 * for the first frame, and a visitor on a slow connection sees the picture
 * until the clip arrives.
 */
export function HeroVideoCard({
  eventId,
  event,
  reload,
}: {
  eventId: string;
  event: EventSettings;
  reload: () => void;
}) {
  const { save, saving, error } = useEventSettingsWrite(eventId);
  const [phase, setPhase] = useState<Phase>("idle");
  const [problem, setProblem] = useState<string | null>(null);
  // Announced through the always-present status line, so a screen reader user
  // hears that an upload or a removal landed.
  const [status, setStatus] = useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  // Set when the document was cleared and the object delete failed: the file
  // is still readable at its tokened URL, so the card offers to try again.
  const [orphaned, setOrphaned] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const retryButton = useRef<HTMLButtonElement>(null);
  const focusAfterDialog = useRef<HTMLElement | null>(null);
  const focusWhenIdle = useRef<HTMLElement | null>(null);

  const busy = phase !== "idle" || saving;

  // Aborted when the card unmounts, which cancels a check in flight and tells
  // an upload in flight to stop reporting. Made in the effect, not once, so a
  // development remount starts with a live one.
  const lifetime = useRef<AbortController | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    lifetime.current = controller;
    return () => controller.abort();
  }, []);

  // Every control here is disabled while busy, which drops focus to the page,
  // and a disabled input refuses focus. So it is moved after the render that
  // re-enables them.
  useEffect(() => {
    if (busy || !focusWhenIdle.current) return;
    focusWhenIdle.current.focus();
    focusWhenIdle.current = null;
  });

  async function upload(file: File) {
    const signal = lifetime.current?.signal;
    if (!signal) return;
    // Nothing here touches state once the card is gone. The write after the
    // upload still happens, because the upload rotated the download token on
    // the fixed path and the event's URL is dead until it learns the new one.
    const live = (update: () => void) => {
      if (!signal.aborted) update();
    };
    live(() => {
      setProblem(null);
      setStatus("Checking the video…");
      setPhase("checking");
    });
    try {
      const refusal = await heroVideoProblem(file, signal);
      if (refusal) {
        live(() => {
          setProblem(refusal);
          setStatus(null);
        });
        return;
      }
      live(() => {
        setPhase("uploading");
        setStatus("Uploading…");
      });
      // One fixed name per event, so replacing the video does not leave the
      // old object behind. storage.rules allows update on the same path.
      const target = ref(storage, heroVideoPath(eventId));
      await uploadBytes(target, file, { contentType: HERO_VIDEO_TYPE });
      const url = await getDownloadURL(target);
      const written = await save({ heroVideo: url });
      if (signal.aborted) return;
      if (written) {
        // The upload overwrote the file a failed removal left behind.
        setOrphaned(false);
        setStatus("Video uploaded");
        reload();
      } else {
        // The upload rotated the download token on the fixed path, so the URL
        // already on the event is dead whether or not this write landed.
        setStatus(null);
        setProblem(
          "The video uploaded but the event could not be updated. Try uploading it again.",
        );
      }
    } catch (cause) {
      live(() => {
        setStatus(null);
        setProblem(
          cause instanceof Error ? cause.message : "The upload failed.",
        );
      });
    } finally {
      live(() => {
        focusWhenIdle.current = fileInput.current;
        setPhase("idle");
      });
    }
  }

  /**
   * Document first, object second, as for the hero image: deleting the object
   * first and failing the write leaves the event pointing at a video that 404s
   * on a page anybody can open. This way round the worst case is a file the
   * event no longer references.
   */
  async function remove() {
    setProblem(null);
    setStatus(null);
    setPhase("removing");
    try {
      if (!(await save({ heroVideo: deleteField() }))) return;
      focusAfterDialog.current = fileInput.current;
      if (await deleteFile()) setStatus("Video removed");
      reload();
    } finally {
      // Closed on failure too: an error left under an open dialog is an error
      // nobody reads.
      setConfirmingRemove(false);
      setPhase("idle");
    }
  }

  /** True when the object is gone, including when it already was. */
  async function deleteFile(): Promise<boolean> {
    try {
      await deleteObject(ref(storage, heroVideoPath(eventId)));
    } catch (cause) {
      if (!isObjectMissing(cause)) {
        setOrphaned(true);
        setProblem(
          "The event no longer shows the video. The file could not be deleted, and anyone who has its link can still open it. Uploading a new video replaces the file.",
        );
        return false;
      }
    }
    setOrphaned(false);
    return true;
  }

  /**
   * Re-reads the event before deleting, because `event` is a one-shot read.
   * If somebody uploaded since the failed removal, the path holds their video
   * and the event points at it; deleting would break the public page.
   */
  async function retryDelete() {
    setProblem(null);
    setStatus(null);
    setPhase("removing");
    let deleted = false;
    try {
      const current = await getDoc(doc(db, "events", eventId));
      if (typeof current.data()?.heroVideo === "string") {
        setOrphaned(false);
        setStatus(
          "A new video was uploaded since, and it replaced the old file.",
        );
        deleted = true;
        reload();
        return;
      }
      deleted = await deleteFile();
      if (deleted) setStatus("File deleted");
    } catch {
      setProblem("The event could not be checked. Try again.");
    } finally {
      focusWhenIdle.current = deleted ? fileInput.current : retryButton.current;
      setPhase("idle");
    }
  }

  return (
    <Card className="border-border shadow-none">
      <CardContent className="space-y-4 py-5">
        <SectionLabel>Hero video</SectionLabel>

        <div className="space-y-1.5">
          <Label htmlFor="hero-video-file">
            {event.heroVideo ? "Replace video" : "Upload video"}
          </Label>
          <FileInput
            ref={fileInput}
            id="hero-video-file"
            testId="hero-video-file"
            accept={HERO_VIDEO_TYPE}
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              // Reset the input, or re-picking the same file after a refusal
              // is a no-op: the change event never fires for an unchanged
              // value.
              e.target.value = "";
              if (file) void upload(file);
            }}
          />
        </div>

        <div
          className="space-y-2 text-xs text-muted-foreground"
          data-testid="hero-video-guidance"
        >
          <p>
            A short silent loop behind the headline, at most{" "}
            {HERO_VIDEO_MAX_SECONDS} seconds and {HERO_VIDEO_MAX_WIDTH} pixels
            wide, as an MP4 of up to {HERO_VIDEO_MAX_BYTES / (1024 * 1024)} MiB.
            Aim for about 3 MiB. The clip should have no audio track. The event
            image is shown as its poster until the clip plays.
          </p>
          {!event.image && (
            <p data-testid="hero-video-no-poster" className="text-foreground">
              This event has no header image yet. Without one, the hero shows a
              plain gradient while the clip loads and wherever a phone blocks
              autoplay, such as in Low Power Mode. Add an image above to use as
              the poster.
            </p>
          )}
          <p>
            To prepare a clip, run this on your computer. It drops the sound,
            limits the width to {HERO_VIDEO_MAX_WIDTH} pixels, keeps the first
            10 seconds and puts the file in the order a browser can start
            playing at once.
          </p>
          <pre
            className="whitespace-pre-wrap break-all rounded-md border border-border bg-muted p-3 font-mono text-foreground"
            data-testid="hero-video-recipe"
          >
            {ENCODE_COMMAND}
          </pre>
        </div>

        {event.heroVideo && (
          <video
            key={event.heroVideo}
            src={event.heroVideo}
            poster={event.image}
            muted
            loop
            controls
            playsInline
            preload="metadata"
            className="aspect-video w-full max-w-full rounded-lg bg-muted sm:max-w-md"
            data-testid="hero-video-preview"
          />
        )}

        {(problem || error) && (
          <p
            role="alert"
            className="flex items-start gap-2 text-sm text-destructive-text"
            data-testid="hero-video-error"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {problem ?? error}
          </p>
        )}

        {orphaned && !event.heroVideo && (
          <Button
            ref={retryButton}
            variant="outline"
            disabled={busy}
            data-testid="retry-delete-hero-video"
            onClick={() => void retryDelete()}
          >
            <Trash2 className="h-4 w-4" />
            {phase === "removing" ? "Deleting…" : "Try deleting the file again"}
          </Button>
        )}

        {/* Always rendered, and visually hidden rather than display:none while
            empty, so the live region is in the accessibility tree before a
            message arrives; one that appears with its text is often not read. */}
        <p
          role="status"
          className={status ? "text-sm text-foreground" : "sr-only"}
          data-testid="hero-video-status"
        >
          {status}
        </p>

        {event.heroVideo && (
          <Button
            variant="destructive"
            disabled={busy}
            className="max-sm:w-full"
            data-testid="remove-hero-video"
            onClick={() => setConfirmingRemove(true)}
          >
            <Trash2 className="h-4 w-4" />
            {phase === "removing" ? "Removing…" : "Remove video"}
          </Button>
        )}

        <Dialog
          open={confirmingRemove}
          onOpenChange={(next) => !next && setConfirmingRemove(false)}
        >
          <DialogContent
            onCloseAutoFocus={(e) => {
              const target = focusAfterDialog.current;
              if (!target) return;
              e.preventDefault();
              focusAfterDialog.current = null;
              target.focus();
            }}
          >
            <DialogHeader>
              <DialogTitle>Remove the hero video?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              The event page shows its image instead. The uploaded file is
              deleted, so its link stops working.
            </p>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setConfirmingRemove(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={busy}
                data-testid="confirm-remove-hero-video"
                onClick={() => void remove()}
              >
                {phase === "removing" ? "Removing…" : "Remove video"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
